import { useTranslation } from 'react-i18next';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { SitterCard as SitterCardComponent } from 'us-react-components/dist/SitterCard/SitterCard';
import { Affiliation, Company, LocalGroup, PagedResponseData, School, Sitter } from 'us-web-services';
import { Routes } from '../../../../../Routes';
import { useDependencies } from '../../../../../dependencies';
import { Button, ButtonType } from '../../../../Button';

type Props = {
  sitters: PagedResponseData<Sitter>;
  hideExperience: boolean;
  setFavorite(isFavorited: boolean, favoriteId: number, sitterId: number): Promise<void>;
  company?: Company;
  school?: School;
  affiliation?: Affiliation;
  localGroup?: LocalGroup;
  params: object;
}

export const GroupSitters = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { services: { tracker }, stores } = useDependencies();

  const handleClick = (id: number): void => {
    const route = generatePath(Routes.sitter.profile, { id });
    navigate(route);
  }

  const handleTracking = (event: string, trackingProps?: Record<string, unknown>): Promise<void> =>
    tracker.track(event, trackingProps);

  const {
    company, school,
    localGroup, affiliation,
    sitters, hideExperience = false, setFavorite,
    params
  } = props;

  const getGroupName = (): string | undefined => {
    if (company) {
      return company.name;
    }
    if (school) {
      return school.name;
    }
    if (affiliation) {
      return affiliation.name;
    }
    if (localGroup) {
      return localGroup.name;
    }

    return t('dashboard.widgets.myGroups.friends');
  }

  const goToGroupPage = (): void => {
    let pathname = Routes.parent.friends;

    if (school) {
      pathname = generatePath(Routes.parent.schools, { alias: school.alias });
    }
    if (affiliation) {
      pathname = generatePath(Routes.parent.affiliations, { alias: affiliation.alias });
    }
    if (localGroup) {
      pathname = generatePath(Routes.parent.localGroups, { alias: localGroup.alias });
    }
    if (company) {
      pathname = generatePath(Routes.parent.company, { alias: company.alias });
    }

    navigate({
      pathname,
      search: `${createSearchParams({
        ...params,
        excludeFavorites: '',
        limit: '',
      })}`,
    });
  }

  return (
    <div className='grid bg-transparent min-h-[316px]'>
      <div className='block'>
        <div className='mb-3'>
          {company && company.logoUrl && (
            <div className='float-left mr-5 inline-block place-content-center'>
              <img src={company.logoUrl} className='h-10 border-2 border-neutral-100 rounded-full px-2' alt={company.name} />
            </div>
          )}
          {!company && (
            <div className='float-left mr-5 mb-4 md:mb-0'>
              <div className='font-bold'>{getGroupName()}</div>
              <div className='text-sm'>
                {sitters.meta.size} {t('dashboard.widgets.myGroups.familyRecommended')} {sitters.meta.size
                  ? t('dashboard.widgets.myGroups.caregivers')
                  : t('dashboard.widgets.myGroups.caregiver')
                }
              </div>
            </div>
          )}
          <Button
            buttonType={ButtonType.secondary}
            className='hidden md:inline-block w-full md:w-fit'
            onClick={goToGroupPage}
          >
            {t('dashboard.widgets.myGroups.seeNearMe')}
            <FontAwesomeIcon icon={faAngleRight} className='pl-2 m-[-1px]' />
          </Button>
        </div>
      </div>
      {company && (
        <div className='block mb-5'>
          <div className='font-bold'>{getGroupName()}</div>
          <div className='text-sm'>
            {sitters.meta.size} {t('dashboard.widgets.myGroups.colleagueRecommended')} {sitters.meta.size
              ? t('dashboard.widgets.myGroups.caregivers')
              : t('dashboard.widgets.myGroups.caregiver')
            }
          </div>
        </div>
      )}
      <div className='grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 mb-8 gap-x-4 gap-y-8 mt-5'>
        {sitters.data.map(sitter => (
          <div
            className='m-b-10'
            key={`${sitter.firstName}-${sitter.id}`}>
            <SitterCardComponent
              sitter={sitter}
              numberOfChildren={stores.parent.getNumberOfChildren()}
              handleClick={handleClick}
              trackingCallback={handleTracking}
              hideExperience={hideExperience}
              setFavorite={setFavorite}
            />
          </div>
        ))}
      </div>
      <Button
        buttonType={ButtonType.secondary}
        className='w-full md:hidden mb-10'
        onClick={goToGroupPage}
      >
        {t('dashboard.widgets.myGroups.seeNearMe')}
        <FontAwesomeIcon icon={faAngleRight} className='pl-2 m-[-1px]' />
      </Button>
    </div>
  );
};
